import { useEffect, useMemo, useRef, useState } from 'react';
import {
	IComponentType,
	IContent360Scene,
	IFaceLandmarkGroup,
	IImageTrackingOrientation,
	IModel3d,
	IRoot,
	ISceneComp,
	ISpatialComponentUnion,
	ITrackingTypes,
	ITransitionCategoryTypes,
	IVideo,
} from '../components/r3f-components/component-data-structure';
import { getFaceLandmarkIdsBySceneId, useGetScreenRelativeComponentIds } from '../components/r3f-components/hooks/useGetScreenRelativeComponentIds';
import { ITransCatTypes } from '../components/r3f-components/transitions';
import { toRadians } from '../components/r3f-components/utils/general';
import { IPlayModelAnimationsData, useDataStore, useSceneStore } from '../zustand';
import { ITransitionPhase } from '../components/r3f-components/transitions/spec';
import { useThree } from '@react-three/fiber';
import React from 'react';
import { Camera, Raycaster, Intersection } from 'three';
import { return360VideoIdBySceneId } from '../components/r3f-components/utils/content360';

export const useEnterOrExit = (transitionCategory: ITransCatTypes): 'enter' | 'exit' => {
	return useMemo(() => {
		return transitionCategory === ITransCatTypes.enter ? ITransitionCategoryTypes.enter : ITransitionCategoryTypes.exit;
	}, [transitionCategory]);
};

export const useIsSceneFlatImageTracked = (scene: ISceneComp | undefined) => {
	const trackingImageDict = useDataStore(({ trackingImageDict }) => trackingImageDict);
	const sceneTrackingImageId = scene?.trackingType === ITrackingTypes.image ? scene.trackingImageId : null;
	const trackingImageOrientation = sceneTrackingImageId && trackingImageDict ? trackingImageDict?.[sceneTrackingImageId].orientation : null;

	return trackingImageOrientation === IImageTrackingOrientation.flat;
};

export const useEntityData = (props: any) => {
	const { id, sceneId, entity } = useMemo(() => {
		const { id, sceneId, ...rest } = props as any;
		return {
			id: id as string,
			sceneId: sceneId as string,
			entity: rest as ISpatialComponentUnion,
		};
	}, []);

	const { position, rotation: r, actions, fontSize, borderWidth, ...restProps } = entity as ISpatialComponentUnion & { fontSize?: number; borderWidth?: number };

	const rotation = useMemo(() => [toRadians(r[0]), toRadians(r[1]), toRadians(r[2])], [r]);

	const restEntityProps = useMemo(() => restProps, []);

	return {
		id,
		sceneId,
		entity,
		rotation,
		actions,
		isVideo: entity.type === IComponentType.Video,
		isModel: entity.type === IComponentType.Model3d,
		fontSize,
		borderWidth,
		restEntityProps,
	};
};

export const useSceneArEntityIds = (sceneId: string, entityType: IComponentType): string[] => {
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	return useMemo(() => {
		if (!componentsById) return [];
		const nonFaceIds = (componentsById[sceneId] as ISceneComp).children.filter((id) => {
			if (!componentsById) return [];
			return componentsById[id].type === entityType;
		});

		const faceLandmarkIds = getFaceLandmarkIdsBySceneId(sceneId, componentsById);
		const faceIds = faceLandmarkIds
			.reduce((acc, landmarkId) => {
				const landmarkGroup = componentsById[landmarkId] as IFaceLandmarkGroup;
				return [...acc, ...landmarkGroup.children];
			}, [] as string[])
			.filter((id) => {
				return componentsById[id].type === entityType;
			});

		return [...nonFaceIds, ...faceIds];
	}, [componentsById, sceneId, entityType]);
};

export const useSceneSrEntityIds = (sceneId: string, entityType: IComponentType): string[] => {
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const sceneRelativeIds = useGetScreenRelativeComponentIds(sceneId, componentsById);
	return useMemo(() => {
		if (!componentsById) return [];

		return sceneRelativeIds.filter((id) => {
			if (!componentsById) return [];
			return componentsById[id].type === entityType;
		});
	}, [componentsById, entityType, sceneRelativeIds]);
};

export const useAllSceneEntityIds = (sceneId: string, entityType: IComponentType): string[] => {
	const arVideoIds = useSceneArEntityIds(sceneId, entityType);
	const srVideoIds = useSceneSrEntityIds(sceneId, entityType);

	return useMemo(() => {
		return [...arVideoIds, ...srVideoIds];
	}, [arVideoIds, srVideoIds]);
};

export const useAllSceneAutoplayVideoIds = (sceneId: string, onlyVisible = false): string[] => {
	const componentsById = useDataStore((state) => state.componentsById);
	const allSceneVideoIds = useAllSceneEntityIds(sceneId, IComponentType.Video);
	return useMemo(
		() =>
			allSceneVideoIds.filter((id) => {
				return !!(componentsById?.[id] as IVideo).autoplay && (onlyVisible ? !(componentsById?.[id] as IVideo).isHidden : true);
			}),
		[allSceneVideoIds, componentsById]
	);
};

export const useGet360VideoIdBySceneId = (sceneId: string): string | null => {
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	if (!componentsById) return null;

	const scene = componentsById[sceneId] as IContent360Scene;
	return scene.content360?.type === 'video' ? return360VideoIdBySceneId(sceneId) : null;
};

export const useModelIdleAnimationData = (sceneId: string): IPlayModelAnimationsData[] => {
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const sceneModel3dIds = useSceneArEntityIds(sceneId, IComponentType.Model3d);
	if (!componentsById) return [];
	const modelIdsWithIdleAnimation = sceneModel3dIds.filter((id) => !!(componentsById[id] as IModel3d).idleAnimation);

	return modelIdsWithIdleAnimation.map((id) => ({
		id,
		isPlayingAnimation: true,
		animationName: (componentsById[id] as IModel3d).idleAnimation,
		animationRepetitions: Infinity,
	}));
};

export const useCameraIsUserFacing = (delay: number = 0) => {
	const timeoutRef = useRef<NodeJS.Timeout>(undefined);
	const componentsById = useDataStore((state) => state.componentsById);
	const rootId = useDataStore((state) => state.rootId);
	const firstSceneId = (componentsById?.[rootId ?? ''] as IRoot)?.children?.[0];
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId ?? firstSceneId);
	const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId ?? firstSceneId);
	const previousSceneTransitionPhase = useSceneStore(({ transitionPhase }) => transitionPhase[previousSceneId]);
	const currentSceneIsFaceTracked = (componentsById?.[activeSceneId] as ISceneComp)?.trackingType === ITrackingTypes.face;
	const currentSceneIsNoTrackingScreen = (componentsById?.[activeSceneId] as ISceneComp)?.trackingType === ITrackingTypes.noTrackingScreen;
	const previousSceneIsFaceTracked = (componentsById?.[previousSceneId] as ISceneComp)?.trackingType === ITrackingTypes.face;
	const firstSceneIsFaceTracked = (componentsById?.[firstSceneId] as ISceneComp)?.trackingType === ITrackingTypes.face;

	const allScenesAreFaceTracked = useDataStore((store) => {
		if (!store.componentsById) return false;
		const arScenes = Object.values(store.componentsById).filter((comp) => comp.type === IComponentType.Scene && comp.trackingType !== ITrackingTypes.noTrackingScreen);
		return arScenes.length > 0 && arScenes.every((comp) => (comp as ISceneComp).trackingType === ITrackingTypes.face);
	});

	const noScenesAreFaceTracked = useDataStore((store) => {
		if (!store.componentsById) return false;
		const arScenes = Object.values(store.componentsById).filter((comp) => comp.type === IComponentType.Scene && comp.trackingType !== ITrackingTypes.noTrackingScreen);
		return arScenes.length > 0 && arScenes.every((comp) => (comp as ISceneComp).trackingType !== ITrackingTypes.face);
	});

	const [cameraIsUserFacing, setCameraIsUserFacing] = useState(() => {
		let initialCameraStateIsFaceTracked = currentSceneIsFaceTracked;
		if (allScenesAreFaceTracked) initialCameraStateIsFaceTracked = true;
		else if (noScenesAreFaceTracked) initialCameraStateIsFaceTracked = false;
		return initialCameraStateIsFaceTracked;
	});

	// If there is no previous transition phase (i.e. jute freshly started) previousTransitionFinished state set to true as no need to await camera switch for any non-existent transitions
	const previousTransitionIsFinished = typeof previousSceneTransitionPhase === 'undefined' ? true : previousSceneTransitionPhase === ITransitionPhase.exitFinish;

	// Delay changes to ensure the camera has switched properly (takes that much time)
	useEffect(() => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			// If all scenes are face tracked, always use user facing camera
			if (allScenesAreFaceTracked) {
				// console.log('USER FACING')
				setCameraIsUserFacing(true);
				return;
			}

			// If no scenes are face tracked, always use rear camera
			if (noScenesAreFaceTracked) {
				// console.log('REAR FACING')
				setCameraIsUserFacing(false);
				return;
			}

			// If current scene is noTrackingScene, use the camera of the previous scene
			if (currentSceneIsNoTrackingScreen) return;

			// If no change in camera orientation required, return early
			if (previousSceneIsFaceTracked && currentSceneIsFaceTracked) {
				setCameraIsUserFacing(true);
				return;
			}

			if (!previousSceneIsFaceTracked && !currentSceneIsFaceTracked) {
				setCameraIsUserFacing(false);
				return;
			}

			// if switch to face tracking, switch back to front camera after awaiting any previous scene exit transitions
			if (!previousSceneIsFaceTracked && currentSceneIsFaceTracked) {
				// console.log('!previousSceneIsFaceTracked && currentSceneIsFaceTracked: ', !previousSceneIsFaceTracked && currentSceneIsFaceTracked)
				// console.log(!previousTransitionIsFinished ? 'USER FACING' : 'REAR FACING')
				setCameraIsUserFacing(previousTransitionIsFinished);
				return;
			}
			// if switch to NON-face tracking, switch front to back camera after awaiting any previous scene exit transitions
			if (previousSceneIsFaceTracked && !currentSceneIsFaceTracked) {
				// console.log('previousSceneIsFaceTracked && !currentSceneIsFaceTracked: ', previousSceneIsFaceTracked && !currentSceneIsFaceTracked);
				// console.log(!previousTransitionIsFinished ? 'USER FACING' : 'REAR FACING')
				setCameraIsUserFacing(!previousTransitionIsFinished);
				return;
			}

			// Fallback is rear facing if none of conditions are satisfied above
			// This should only be reached for the first scene, transitions will be managed by the 2x if statements above
			// console.log('REAR FACING')
			setCameraIsUserFacing(firstSceneIsFaceTracked ? true : false);
		}, delay);
	}, [currentSceneIsFaceTracked, previousSceneIsFaceTracked, previousTransitionIsFinished, delay]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	return cameraIsUserFacing;
};

// Modified version of https://github.com/pmndrs/drei/blob/master/src/core/useCamera.tsx
// Drei hook crashes if camera is falsey - this prevents the crash
export function useGetRaycastFromCamera(camera: React.RefObject<Camera>, props?: Partial<Raycaster>) {
	const pointer = useThree((state) => state.pointer);
	const [raycast] = React.useState(() => {
		const raycaster = new Raycaster();
		/**
		 * applyProps is an internal method of r3f and
		 * therefore requires its first arg to be an
		 * "Instance" a term used with the Reconciler
		 * so we have an expect error to mask this
		 */
		// @ts-expect-error
		if (props) applyProps(raycaster, props, {});
		return function (_: Raycaster, intersects: Intersection[]): void {
			if (!camera.current) return;
			raycaster.setFromCamera(pointer, camera.current);
			// @ts-expect-error
			const rc = this.constructor.prototype.raycast.bind(this);
			if (rc) rc(raycaster, intersects);
		};
	});
	return raycast;
}

export const useGetCurrentSceneId = () => {
	const firstSceneId = useDataStore((state) => (state.componentsById?.[state.rootId as string] as IRoot)?.children[0]);
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	return activeSceneId || firstSceneId;
};

export const useGetFirstSceneId = () => {
	return useDataStore((state) => (state.componentsById?.[state.rootId as string] as IRoot)?.children[0]);
};

export const useGetPreviousSceneId = () => {
	return useSceneStore((state) => state.previousSceneId);
};
